import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { Session } from '@providers/session/session.service';

/**
 * Response from POST /avatar endpoint (create avatar).
 */
export interface CreateAvatarResponse {
  message: string;
  success: boolean;
  data: {
    avatarId: string;
    profileId: string;
    status: string;
    estimatedTime: string;
    createdAt: string;
  };
}

/**
 * Response from GET /avatar/status endpoint.
 */
export interface AvatarStatusResponse {
  success: boolean;
  data: {
    processing?: {
      avatarId: string;
      status: string;
      createdAt: string;
      estimatedTimeRemaining?: string;
    } | null;
    current?: {
      avatarId: string;
      status: string;
      processedAt: string;
      hasImages: boolean;
    } | null;
    lastFailed?: {
      avatarId: string;
      status: string;
      failedAt: string;
      error?: string;
    } | null;
    status?: string; // 'DEFAULT' if no avatars
  };
}

/**
 * Response from GET /avatar/image endpoint.
 */
export interface AvatarImageResponse {
  success: boolean;
  data: {
    imageType: string;
    imageUrl: string;
    width: number;
    height: number;
    isDefault: boolean;
    avatarId: string;
  };
}

/**
 * Error response from avatar API.
 */
export interface AvatarErrorResponse {
  success: boolean;
  error: {
    code: string;
    message: string;
  };
}

/**
 * Service for avatar creation API calls.
 * Communicates with avatar microservice at environment.API_AVATAR.
 *
 * @exports
 * @class AvatarCreateService
 */
@Injectable()
export class AvatarCreateService {
  private readonly http = inject(HttpClient);
  /**
   * The `session` property is protected (not private) to allow access from derived classes
   * or other services that need to interact with the session state.
   */
  protected readonly session = inject(Session); // eslint-disable-line kawaī/inject-private-readonly

  /**
   * Get JWT token from session.
   *
   * @private
   * @returns {string | null} JWT token or null if not available.
   * @memberof AvatarCreateService
   */
  get token(): string | null {
    return this.session.token;
  }

  /**
   * Get HTTP headers with JWT token for authentication.
   *
   * @private
   * @param {string} contentType Content-Type header value.
   * @returns {unknown} HTTP headers with Authorization bearer token.
   * @memberof AvatarCreateService
   */
  private getHeaders(
    contentType = 'application/json'
  ): Record<string, unknown> {
    return {
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': contentType
      }
    };
  }

  /**
   * Get HTTP headers for multipart/form-data (no Content-Type header, let browser set it).
   *
   * @private
   * @returns {unknown} HTTP headers with Authorization bearer token only.
   * @memberof AvatarCreateService
   */
  private getMultipartHeaders(): Record<string, unknown> {
    return {
      headers: {
        Authorization: `Bearer ${this.token}`
        // Don't set Content-Type for multipart, browser will set it with boundary
      }
    };
  }

  /**
   * Get avatar image URL for specific image type.
   * GET /avatar/image?imageType={type}&profileId={id}.
   *
   * @param {string} imageType - Type of image (HOMEPAGE, SKIN, NOURISH, 10X, ACTIVE, PERSONA, PRODIGY, 360, PROFILE).
   * @param {string} [profileId] - Optional profile ID (defaults to JWT username if not provided).
   * @returns {Observable<AvatarImageResponse>} Observable with avatar image data.
   * @memberof AvatarCreateService
   */
  getAvatarImage(
    imageType: string,
    profileId?: string
  ): Observable<AvatarImageResponse> {
    let url = `${environment.API_AVATAR}/avatar/image?imageType=${imageType}`;
    if (profileId) {
      url += `&profileId=${profileId}`;
    }
    return this.http.get<AvatarImageResponse>(url, this.getHeaders());
  }

  /**
   * Get current avatar status (processing, current, failed).
   * GET /avatar/status.
   *
   * @returns {Observable<AvatarStatusResponse>} Observable with avatar status.
   * @memberof AvatarCreateService
   */
  getAvatarStatus(): Observable<AvatarStatusResponse> {
    return this.http.get<AvatarStatusResponse>(
      `${environment.API_AVATAR}/avatar/status`,
      this.getHeaders()
    );
  }

  /**
   * Upload avatar image to create new avatar.
   * POST /avatar.
   *
   * Supports two upload methods:
   * 1. Multipart form-data (native file upload)
   * 2. JSON with base64 encoded image.
   *
   * @param {FormData | object} data - FormData with sourceImage and sourceType, or JSON object with base64 image.
   * @param {boolean} isMultipart - True if data is FormData, false if JSON.
   * @returns {Observable<CreateAvatarResponse>} Observable with avatar creation response.
   * @memberof AvatarCreateService
   */
  uploadAvatar(
    data: FormData | { sourceType: string; sourceImage: string },
    isMultipart = true
  ): Observable<CreateAvatarResponse> {
    const url = `${environment.API_AVATAR}/avatar`;
    const headers = isMultipart
      ? this.getMultipartHeaders()
      : this.getHeaders('application/json');

    return this.http.post<CreateAvatarResponse>(url, data, {
      ...headers,
      timeout: 2 * 60 * 1000 // 2 minutes timeout for avatar upload
    });
  }
}
